import { Forward, ForwardOutlined, Timeline } from '@mui/icons-material';
import { Box, CardContent, Chip, styled, Tooltip, Typography } from '@mui/material';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import PublishedImg from '../../assets/verified.svg';
import { AI_APP_STORE_LABEL } from '../../constants/promptLabels';
import { useAutoScrollHighlight } from '../../hooks/useAutoScrollHighlight';
import { useLikePrompt } from '../../hooks/useLikePrompt';
import { usePinPrompt } from '../../hooks/usePinPrompt';
import { usePromptDrawer } from '../../hooks/usePromptDrawer';
import { myFolder } from '../../redux/consts';
import { selectFolders } from '../../redux/reducers/folderReducer';
import { LikeContainer } from '../../theme/CustomComponents';
import { CustomTypographyVariant } from '../../theme/theme';
import { PromptLocation } from '../../Types/enums';
import { Prompt } from '../../Types/prompt';
import { getPromptOwnerName } from '../../util/promptMetadata';
import AutoTruncatingTypographyWithTooltip from '../AutoTruncatingTypographyWithTooltip';
import { useAuth } from '../Providers/AuthProvider';
import ToolIcons from '../ToolIcons';
import PinContainer from './PinContainer';
import PromptPinOptionsMenu from './PromptPinOptionsMenu';
import PromptRunButton from './PromptRunButton';

const PromptCardComponent = styled('div', {
  shouldForwardProp: (prop) => prop !== 'location',
})<{ location: PromptLocation }>(({ location }) => ({
  height: '100%',
  ':hover': {
    cursor: 'pointer',
    border: '1px solid #F900D3',
  },
  borderRadius: '12px',
  outline: 0,
  WebkitTapHighlightColor: 'transparent',
  display: 'block',
  WebkitTextDecoration: 'none',
  textDecoration: 'none',
  backgroundColor: '#fff',
  boxSizing: 'border-box',
  position: 'relative',
  border: '1px solid rgb(244, 244, 244)',
  color: '#070154',
  ...(location === PromptLocation.FEATURED_PROMPTS
    ? {
        padding: '10px 0px 10px 10px',
        boxShadow: '0px 2px 4px 0px #0701521F',
        width: '300px',
      }
    : {
        padding: 'auto',
        boxShadow: 'none',
        width: '100%',
      }),
}));

const StyledCardContent = styled(CardContent, {
  shouldForwardProp: (prop) => prop !== 'location',
})(({ location }: { location: PromptLocation }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  gap: '10px',
  height: '100%',
  ...(location === PromptLocation.FEATURED_PROMPTS
    ? { padding: '0px 0px 10px' }
    : {
        padding: '10px',
        '&:last-child': {
          paddingBottom: '10px',
        },
      }),
}));

const CardLeftSection = styled('div', {
  shouldForwardProp: (prop) => prop !== 'location',
})(({ location }: { location: PromptLocation }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '5px',
  overflow: 'hidden',
  padding: '0px 10px 0px',
  flexGrow: 1,
  height: location === PromptLocation.FEATURED_PROMPTS ? '40px' : '100%',
}));

const CardRightSection = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  alignItems: 'flex-end',
  padding: '0px 10px 0px',
  minWidth: '40px',
  height: 'calc(100% - 20px)',
});

type CardStyle = {
  title: {
    variant: CustomTypographyVariant;
    fontWeight: string;
    fontSize: string;
  };
  description: {
    variant: CustomTypographyVariant;
    fontSize: string;
    color: string;
  };
};

const locationCardStyleMap: Record<PromptLocation, CardStyle> = {
  [PromptLocation.PROMPTS_PAGE]: {
    title: {
      variant: 'subtitle2',
      fontWeight: '500',
      fontSize: '14px',
    },
    description: {
      variant: 'body2',
      fontSize: '12px',
      color: '#757575',
    },
  },
  [PromptLocation.FEATURED_PROMPTS]: {
    title: {
      variant: 'caption1',
      fontWeight: '500',
      fontSize: '12px',
    },
    description: {
      variant: 'caption2',
      fontSize: '12px',
      color: '#757575',
    },
  },
  [PromptLocation.PROMPT_LIST]: {
    title: {
      variant: 'h4',
      fontWeight: '700',
      fontSize: '18px',
    },
    description: {
      variant: 'body2',
      fontSize: '14px',
      color: '#50658E',
    },
  },
};

const PromptCard = ({
  prompt,
  publishedOnly,
  autoScroll,
  onScrolled,
  isAIAppStore = false,
  location = PromptLocation.PROMPTS_PAGE,
  onClick,
}: {
  prompt: Prompt;
  publishedOnly?: boolean;
  autoScroll?: boolean;
  onScrolled?: () => void;
  isAIAppStore?: boolean;
  location?: PromptLocation;
  onClick?: (prompt: Prompt) => void;
}): JSX.Element => {
  const { currentFolder } = useSelector(selectFolders);

  const containerRef = useRef<HTMLDivElement>(null);
  const [visibleTags, setVisibleTags] = useState<string[]>([]);
  const [hiddenTags, setHiddenTags] = useState<string[]>([]);

  const { currentUser } = useAuth();
  const { ref, highlightSx } = useAutoScrollHighlight({ autoScroll, onScrolled });
  const { openPromptDrawer } = usePromptDrawer({});

  const cardStyle = locationCardStyleMap[location];
  const iconSize: object = { height: '14px', width: '14px' };
  const iconStyle = {
    ...iconSize,
    color: 'gray',
  };

  const {
    handlePinMenuSetup,
    handlePinMenuClose,
    getPinTooltipText,
    pinOptionsAnchorEl,
    pinOptionsMenuOpen,
  } = usePinPrompt({ redirectOnPin: !isAIAppStore });
  const { handleLike, getLikeTooltipText } = useLikePrompt();

  const handleOnPinClick = useCallback(
    (e: React.MouseEvent<Element>) => {
      handlePinMenuSetup(e as React.MouseEvent<HTMLElement>, prompt);
    },
    [handlePinMenuSetup, prompt]
  );

  // Handler for like click
  const handleLiked = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      e.stopPropagation();
      // Force blur to close any open tooltips
      (e.currentTarget as HTMLElement).blur();
      handleLike(prompt);
    },
    [prompt, handleLike]
  );

  const getTextWidth = (text: string, style: { fontSize: string; fontWeight: number }) => {
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    if (context) {
      context.font = `${style.fontWeight} ${style.fontSize} Arial`;
      return context.measureText(text).width + 10;
    }
    return 0;
  };

  useEffect(() => {
    const calculateTagVisibility = () => {
      if (!containerRef.current) return;

      const containerWidth = containerRef.current.offsetWidth;
      const visible: string[] = [];
      const hidden: string[] = [];
      const labels = [
        prompt.solutionType ?? 'Prompt',
        `Owner: ${currentUser?.id === prompt.ownerId ? 'me' : getPromptOwnerName(prompt)}`,
        ...(isAIAppStore
          ? prompt.labels.filter((label) => label !== AI_APP_STORE_LABEL)
          : prompt.labels),
      ];

      let currentWidth = 40;
      let hiddenAny = false;

      for (const label of labels) {
        const tagWidth =
          getTextWidth(label, {
            fontSize: '12px',
            fontWeight: 500,
          }) + 24;

        if (hiddenAny || currentWidth + tagWidth > containerWidth) {
          hiddenAny = true;
          hidden.push(label);
        } else {
          visible.push(label);
          currentWidth += tagWidth;
        }
      }

      setVisibleTags(visible);
      setHiddenTags(hidden);
    };

    calculateTagVisibility();
    window.addEventListener('resize', calculateTagVisibility); // Recalculate on resize
    return () => window.removeEventListener('resize', calculateTagVisibility);
  }, [
    prompt.labels,
    containerRef,
    currentUser?.id,
    prompt.ownerId,
    prompt.ownerName,
    prompt.solutionType,
    isAIAppStore,
    prompt,
  ]);

  const handleOnClick = useCallback(() => {
    if (onClick) {
      return onClick(prompt);
    }

    openPromptDrawer(prompt.id);
  }, [onClick, openPromptDrawer, prompt]);

  const showTags = () => {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: 'calc(100% - 10px)',
        }}
      >
        <div
          ref={containerRef}
          id={prompt.id}
          style={{
            display: 'flex',
            gap: '4px',
            flexWrap: 'wrap',
            minHeight: '28px',
            overflow: 'hidden',
            flex: 1,
          }}
        >
          {/* Main chip for author */}
          {visibleTags.length > 0 && (
            <Chip
              variant="outlined"
              label={
                <span style={{ display: 'flex', alignItems: 'center', color: '#070154' }}>
                  {prompt.published && !publishedOnly ? (
                    <Tooltip title="Published">
                      <img
                        style={{ marginRight: '5px', width: '14px', height: '14px' }}
                        src={PublishedImg}
                        alt="Published"
                      />
                    </Tooltip>
                  ) : null}
                  {visibleTags[0]}
                </span>
              }
              sx={{ fontSize: '12px', fontWeight: '500', height: '24px' }}
            />
          )}

          {/* Render visible labels */}
          {visibleTags.slice(1).map((tag, index) => (
            <Chip
              label={tag}
              key={index}
              sx={{
                fontSize: '12px',
                fontWeight: '500',
                height: '24px',
                color: '#070154',
                backgroundColor: '#E8EEF8',
                margin: '2px',
              }}
            />
          ))}

          {/* Overflow chip to indicate hidden labels */}
          {hiddenTags.length > 0 && (
            <Tooltip title={hiddenTags.join(', ')}>
              <Chip
                label={`+${hiddenTags.length}`}
                sx={{
                  fontSize: '13px',
                  fontWeight: '500',
                  height: '24px',
                  color: '#070154',
                  backgroundColor: '#E8EEF8',
                  margin: '2px',
                }}
              />
            </Tooltip>
          )}
        </div>
        {location === PromptLocation.FEATURED_PROMPTS && (
          <PromptRunButton prompt={prompt} isAIAppStore={isAIAppStore} />
        )}
      </div>
    );
  };

  return (
    <PromptCardComponent location={location} onClick={handleOnClick} sx={highlightSx} ref={ref}>
      <StyledCardContent location={location}>
        <CardLeftSection location={location}>
          {location === PromptLocation.PROMPTS_PAGE && (
            <div style={{ display: 'flex', width: '100%', alignItems: 'center' }}>
              {currentFolder && currentFolder.id !== myFolder.id && (
                <PromptPinOptionsMenu
                  prompt={prompt}
                  anchorEl={pinOptionsAnchorEl}
                  open={pinOptionsMenuOpen}
                  onClose={handlePinMenuClose}
                  isAIAppStore={isAIAppStore}
                />
              )}
              <Tooltip
                title={
                  <Typography variant="caption">
                    {getPinTooltipText(prompt.pinned || false, prompt.folderPinned || false)}
                  </Typography>
                }
              >
                <span>
                  <PinContainer
                    onClick={handleOnPinClick}
                    pinned={prompt.pinned}
                    folderPinned={prompt.folderPinned}
                  />
                </span>
              </Tooltip>

              {prompt.featured && (
                <Box
                  sx={{
                    backgroundColor: '#E8EEF8',
                    borderRadius: '4px',
                    color: '#0047FF',
                    width: '85px',
                    height: '20px',
                    display: 'flex',
                    justifyContent: 'center',
                    gap: '10px',
                    alignItems: 'center',
                    marginLeft: '10px',
                  }}
                >
                  <Typography sx={{ fontSize: '12px', fontWeight: '500' }}>FEATURED</Typography>
                </Box>
              )}
            </div>
          )}
          {location === PromptLocation.PROMPT_LIST ? (
            <Box display="flex" gap={1} my={1}>
              {(prompt.pinned || prompt.folderPinned) && (
                <div style={{ alignSelf: 'center' }}>
                  <PinContainer
                    pinned={prompt.pinned}
                    folderPinned={prompt.folderPinned}
                    iconOnly
                  />
                </div>
              )}
              <AutoTruncatingTypographyWithTooltip
                text={prompt.title}
                variant={cardStyle.title.variant}
                sx={{
                  fontWeight: cardStyle.title.fontWeight,
                  fontSize: cardStyle.title.fontSize,
                  pr: 1,
                }}
              />
            </Box>
          ) : (
            <AutoTruncatingTypographyWithTooltip
              text={prompt.title}
              variant={cardStyle.title.variant}
              sx={{
                fontWeight: cardStyle.title.fontWeight,
                fontSize: cardStyle.title.fontSize,
              }}
            />
          )}
          <AutoTruncatingTypographyWithTooltip
            text={prompt.description}
            variant={cardStyle.description.variant}
            sx={{
              WebkitLineClamp: 1,
              fontWeight: '400',
              color: cardStyle.description.color,
              fontSize: cardStyle.description.fontSize,
              lineHeight: '14px',
            }}
            useHighlighter={false}
          />
          {location === PromptLocation.PROMPTS_PAGE && showTags()}
          {location === PromptLocation.PROMPT_LIST && <ToolIcons tools={prompt.tools} />}
        </CardLeftSection>
        {location !== PromptLocation.PROMPT_LIST && (
          <CardRightSection>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-start',
                alignItems: 'flex-end',
                gap: '8px',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <Tooltip
                  placement="top"
                  title={
                    <Typography variant="caption">
                      {getLikeTooltipText(prompt.liked || false, isAIAppStore)}
                    </Typography>
                  }
                >
                  <LikeContainer
                    aria-labelledby={`like-label-${prompt.id}`}
                    onClick={handleLiked}
                    liked={prompt.liked || false}
                    style={{ transform: 'rotate(-90deg)' }}
                  >
                    {prompt.liked ? <Forward /> : <ForwardOutlined />}
                  </LikeContainer>
                </Tooltip>
                <Typography variant="caption" sx={{ color: 'gray' }}>
                  {prompt.likedCount || 0}
                </Typography>
              </div>
              <Tooltip title={`${isAIAppStore ? 'App' : 'Prompt'} Usage`}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <Timeline
                    sx={{
                      ...iconStyle,
                      paddingRight: '2px',
                    }}
                  />
                  <Typography variant="caption" sx={{ color: 'gray' }}>
                    {prompt.usageCount}
                  </Typography>
                </div>
              </Tooltip>
            </div>
            {location === PromptLocation.PROMPTS_PAGE && (
              <PromptRunButton prompt={prompt} isAIAppStore={isAIAppStore} />
            )}
          </CardRightSection>
        )}
      </StyledCardContent>
      {location === PromptLocation.FEATURED_PROMPTS && showTags()}
    </PromptCardComponent>
  );
};

export default PromptCard;
